import React, { useState, useEffect } from 'react';
import { nurseAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { toast } from 'react-toastify';
import { Video } from 'lucide-react';
import SessionsTableLoader from '../../components/SessionsTableLoader';
import { patientAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalSessions: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const itemsPerPage = 10;

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchSessions();
  }, [debouncedSearchTerm, statusFilter, pagination.currentPage]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const user = tokenManager.getUser();
      
      // Get patient profile
      const patientResponse = await patientAPI.getMyProfile();
      if (!patientResponse?.patientProfile) {
        throw new Error('Patient profile not found');
      }

      // Fetch nurse sessions using patient profile ID with pagination and filters
      const nurseSessionsResponse = await nurseAPI.getPatientNurseSessions(
        patientResponse.patientProfile._id,
        {
          page: pagination.currentPage,
          limit: pagination.limit,
          status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
          search: debouncedSearchTerm
        }
      );

      // Map nurse sessions with correct patient data
      const nurseSessions = (nurseSessionsResponse?.sessions || []).map(session => ({
        ...session,
        providerType: 'nurse',
        providerName: session.nurseId?.name,
        providerId: session.nurseId?._id,
        patient: {
          name: user.name,
          profileImage: patientResponse.patientProfile.profileImage
        }
      }));

      // Update pagination data from API response
      if (nurseSessionsResponse?.pagination) {
        setPagination(prev => ({
          ...prev,
          currentPage: parseInt(nurseSessionsResponse.pagination.currentPage),
          totalPages: parseInt(nurseSessionsResponse.pagination.totalPages),
          totalSessions: parseInt(nurseSessionsResponse.pagination.totalSessions),
          hasNextPage: parseInt(nurseSessionsResponse.pagination.currentPage) < parseInt(nurseSessionsResponse.pagination.totalPages),
          hasPrevPage: parseInt(nurseSessionsResponse.pagination.currentPage) > 1
        }));
      }

      setSessions(nurseSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error(error.message || 'Failed to fetch sessions');
      setSessions([]);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalPages: 1,
        totalSessions: 0,
        hasNextPage: false,
        hasPrevPage: false
      }));
    } finally {
      setLoading(false);
    }
  };

  const formatSessionType = (type) => {
    const types = {
      'monthly_checkin': 'Monthly Check-in',
      'initial_assessment': 'Initial Assessment',
      'follow_up': 'Follow-up Session'
    };
    return types[type] || type;
  };

  const formatStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      missed: 'bg-red-100 text-red-800',
      in_progress: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Filter sessions based on search term and status
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = (
      session.providerName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      formatSessionType(session.sessionType).toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    const matchesStatus = statusFilter === 'All' || session.status === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  // Handle page change
  const handlePageChange = (page) => {
    // Ensure page is within valid range
    if (page < 1 || page > pagination.totalPages) {
      return;
    }
    
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  // Add filter handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Reset to page 1 when search changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    // Reset to page 1 when status changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  return (
    <div className="">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">My Sessions</h1>
        <p className="mt-2 text-sm text-gray-600">View and manage all your therapy sessions</p>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by nurse name..."
                className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full md:w-48">
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="block w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="All">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="missed">Missed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <SessionsTableLoader />
      ) : filteredSessions.length > 0 ? (
        <>
          <div className="w-full">
            {/* Desktop Table Header */}
            <div className="hidden md:grid grid-cols-5 gap-4 items-center text-xs bg-gray-100 font-medium text-gray-600 uppercase tracking-wider mb-4 px-4 py-4 rounded-lg">
              <div>Date & Time</div>
              <div>Provider</div>
              <div>Type</div>
              <div>Mode</div>
              <div>Status</div>
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block">
              {filteredSessions.map((session, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-5 gap-4 items-center border rounded-xl px-4 py-4 my-2 bg-white shadow-sm hover:bg-blue-50 transition border-gray-200 hover:border-blue-300"
                >
                  <div className="text-sm text-gray-900">
                    <div className="font-medium">
                      {new Date(session.date).toLocaleDateString('en-US', { 
                        month: 'numeric', 
                        day: 'numeric', 
                        year: 'numeric'
                      })}
                    </div>
                    <div className="text-gray-500">
                      {session.startTime} - {session.endTime}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <img
                      src={session.nurseId?.profileImage ? getImageUrl(session.nurseId.profileImage) : "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                      alt={session.providerName}
                      className="w-8 h-8 rounded-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                      }}
                    />
                    <div>
                      <span className="text-sm text-gray-900">{session.providerName}</span>
                      <div className="text-xs text-gray-500">Nurse</div>
                    </div>
                  </div>

                  <div>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                      {formatSessionType(session.sessionType)}
                    </span>
                  </div>

                  <div className="text-sm text-gray-700">
                    {session.sessionMode === 'video' ? 'Video Call' : 'Phone Call'}
                  </div>

                  <div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        session.status === "scheduled"
                          ? "bg-blue-100 text-blue-600"
                          : session.status === "in_progress"
                          ? "bg-yellow-100 text-yellow-600"
                          : session.status === "completed"
                          ? "bg-green-100 text-green-600"
                          : session.status === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : session.status === "missed"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {session.status === "in_progress" ? "In Progress" : 
                       session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {filteredSessions.map((session, idx) => (
                <div
                  key={idx}
                  className="bg-white border rounded-xl p-4 shadow-sm hover:bg-blue-50 transition border-gray-200 hover:border-blue-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <img
                        src={session.nurseId?.profileImage ? getImageUrl(session.nurseId.profileImage) : "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={session.providerName}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                        }}
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 block">
                          {session.providerName}
                        </span>
                        <span className="text-xs text-gray-500">Nurse</span>
                      </div>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        session.status === "scheduled"
                          ? "bg-blue-100 text-blue-600"
                          : session.status === "in_progress"
                          ? "bg-yellow-100 text-yellow-600"
                          : session.status === "completed"
                          ? "bg-green-100 text-green-600"
                          : session.status === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : session.status === "missed"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {session.status === "in_progress" ? "In Progress" : 
                       session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">
                          {new Date(session.date).toLocaleDateString('en-US', { 
                            month: 'numeric', 
                            day: 'numeric', 
                            year: 'numeric'
                          })}
                        </div>
                        <div className="text-gray-500">
                          {session.startTime} - {session.endTime}
                        </div>
                      </div>
                      <div className="text-sm text-gray-700">
                        {session.sessionMode === 'video' ? 'Video Call' : 'Phone Call'}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                        {formatSessionType(session.sessionType)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalSessions)} of {pagination.totalSessions} results
                </p>
              </div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
                    pagination.currentPage === 1 ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                >
                  <span className="sr-only">First</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (pagination.totalPages <= 5) return true;
                    if (page === 1 || page === pagination.totalPages) return true;
                    if (Math.abs(page - pagination.currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, index, array) => {
                    if (index > 0 && page - array[index - 1] > 1) {
                      return [
                        <span key={`ellipsis-${page}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            pagination.currentPage === page
                              ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pagination.currentPage === page
                            ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
                    pagination.currentPage === pagination.totalPages ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                >
                  <span className="sr-only">Last</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8 sm:py-12 bg-white rounded-xl border border-gray-200">
          <div className="flex flex-col items-center">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4 px-4">
              {debouncedSearchTerm || statusFilter !== 'All'
                ? "No sessions match your current filters. Try adjusting your search criteria."
                : "There are no sessions available at the moment."}
            </p>
            {(debouncedSearchTerm || statusFilter !== 'All') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('All');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions; 